import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Edit,
  Paperclip,
  User,
  Clock,
  MessageCircle,
  Trash2,
  X,
  FileText,
  Eye,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import moment from 'moment';
import { capitalizeFirst, getStatusColor } from '../../utils';
import { SupportTicket, MT5AccountUser } from '../../utils/types';
import { supportTicketAPI } from '../../services/supportTicketService';
import api, { mt5API } from '../../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import ConfirmationModal from '../common/ConfirmationModal';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import toast from 'react-hot-toast';
import SourceCombobox from '../common/SourceCombobox';
import { SUPPORT_CATEGORIES } from '../../constants/supportOptions';
import { IMAGE_API_BASE_URL } from '../../config/api';
import LoaderOverlay from '../common/LoaderOverlay';

export interface EditableAttachment {
  id: string;
  name: string;
  size?: number;
  url: string;
  path?: string;
  originalAttachment?: any;
  file?: File;
  isExisting: boolean;
}

interface TicketCardProps {
  ticket: SupportTicket;
  onEdit?: (ticketId: string) => void;
  onDelete?: (ticketId: string) => void;
}

const getBackendHost = (): string => {
  const envUrl =
    IMAGE_API_BASE_URL ||
    process.env.REACT_APP_IMAGE_API_BASE_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    'http://localhost:5000';
  return envUrl.replace(/\/api\/?$/, '').replace(/\/$/, '');
};

const getAttachmentUrl = (attachmentPath: string): string => {
  if (!attachmentPath) return '';

  if (
    attachmentPath.startsWith('http://') ||
    attachmentPath.startsWith('https://') ||
    attachmentPath.startsWith('blob:') ||
    attachmentPath.startsWith('data:')
  ) {
    return attachmentPath;
  }

  const backendHost = getBackendHost();
  let normalizedPath = attachmentPath.replace(/\\/g, '/');

  if (
    !normalizedPath.startsWith('/uploads/') &&
    !normalizedPath.startsWith('http')
  ) {
    if (normalizedPath.startsWith('uploads/')) {
      normalizedPath = '/' + normalizedPath;
    } else {
      normalizedPath =
        '/uploads/' +
        (normalizedPath.startsWith('/')
          ? normalizedPath.slice(1)
          : normalizedPath);
    }
  }

  const cleanPath = normalizedPath.startsWith('/')
    ? normalizedPath
    : '/' + normalizedPath;

  if (cleanPath.startsWith(backendHost)) {
    return cleanPath;
  }

  return `${backendHost}${cleanPath}`;
};

const isImageFile = (fileNameOrType: string): boolean => {
  if (!fileNameOrType) return false;
  if (fileNameOrType.startsWith('image/')) return true;
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
  const ext = fileNameOrType.split('.').pop()?.toLowerCase() || '';
  return imageExtensions.includes(ext);
};

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

interface AttachmentImageProps {
  src: string;
  alt?: string;
  className?: string;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

const AttachmentImage = ({
  src,
  alt,
  className,
  onError,
}: AttachmentImageProps) => {
  const [blobUrl, setBlobUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;
    let createdUrl = '';

    if (!src) {
      setLoading(false);
      return;
    }

    if (src.startsWith('blob:') || src.startsWith('data:')) {
      setBlobUrl(src);
      setLoading(false);
      return;
    }

    const loadImageBlob = async () => {
      try {
        setLoading(true);
        const response = await api.get(src, { responseType: 'blob' });
        if (response.data && isMounted) {
          createdUrl = URL.createObjectURL(response.data);
          setBlobUrl(createdUrl);
        }
      } catch (err) {
        console.error('Error fetching attachment blob:', err);
        if (isMounted) {
          setBlobUrl(src);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadImageBlob();

    return () => {
      isMounted = false;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [src]);

  if (loading) {
    return (
      <div className="w-full h-full min-h-[100px] bg-muted/30 flex items-center justify-center">
        <LoaderOverlay isLoading={loading} loadingText="Tickets Loading..." />
      </div>
    );
  }

  return (
    <img
      src={blobUrl || src}
      alt={alt}
      onError={onError}
      className={className}
    />
  );
};

const TicketCard = ({ ticket, onEdit, onDelete }: TicketCardProps) => {
  const formatDate = (dateString: string) => {
    return moment(dateString).format('MMM DD, YYYY [at] hh:mm A');
  };

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editCategory, setEditCategory] = useState(ticket.category);
  const [editDescription, setEditDescription] = useState(
    ticket.issueDescription
  );
  const [editAccountNumber, setEditAccountNumber] = useState(
    ticket.accountNumber || ''
  );
  const [editAttachments, setEditAttachments] = useState<EditableAttachment[]>(
    []
  );
  const [attachmentError, setAttachmentError] = useState<string>('');
  const [failedImageUrls, setFailedImageUrls] = useState<
    Record<string, boolean>
  >({});
  const [previewModalUrl, setPreviewModalUrl] = useState<string | null>(null);
  const [previewModalTitle, setPreviewModalTitle] = useState<string>('');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isAdminResponseExpanded, setIsAdminResponseExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mt5Accounts, setMt5Accounts] = useState<MT5AccountUser[]>([]);
  const [loadingMt5Accounts, setLoadingMt5Accounts] = useState(false);

  const handleImageError = (url: string) => {
    setFailedImageUrls(prev => ({ ...prev, [url]: true }));
  };

  const editCategoryLabel =
    SUPPORT_CATEGORIES.find(option => option.value === editCategory)?.label ||
    editCategory;

  const mt5AccountOptions = mt5Accounts.map(account => ({
    value: (account.mt5Login || account.Login || '').toString(),
    label: `${account.mt5Login || account.Login || ''} - ${account.name || account.Name || ''}`,
  }));

  const selectedMt5AccountLabel = mt5AccountOptions.find(
    option => option.value === editAccountNumber
  )?.label;

  useEffect(() => {
    const fetchMt5Accounts = async () => {
      setLoadingMt5Accounts(true);
      try {
        const response = await mt5API.getUsers();

        const accountsData = response?.data || response;

        if (accountsData && Array.isArray(accountsData)) {
          setMt5Accounts(accountsData);
        }
      } catch (error) {
        console.error('Error fetching MT5 accounts:', error);
      } finally {
        setLoadingMt5Accounts(false);
      }
    };

    fetchMt5Accounts();
  }, []);

  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    if (editAttachments.length + selectedFiles.length > 2) {
      setAttachmentError('Maximum 2 files allowed in total.');
      return;
    }

    for (const file of selectedFiles) {
      if (file.size > 5 * 1024 * 1024) {
        setAttachmentError(`File "${file.name}" exceeds 5MB limit.`);
        return;
      }
    }

    setAttachmentError('');
    const newItems: EditableAttachment[] = selectedFiles.map(file => ({
      id: `new-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      name: file.name,
      size: file.size,
      url: URL.createObjectURL(file),
      file,
      isExisting: false,
    }));

    setEditAttachments(prev => [...prev, ...newItems]);
    e.target.value = '';
  };

  const handleReplaceFile = (
    id: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setAttachmentError(`File "${file.name}" exceeds 5MB limit.`);
      return;
    }

    setAttachmentError('');
    const newUrl = URL.createObjectURL(file);

    setEditAttachments(prev =>
      prev.map(item => {
        if (item.id === id) {
          return {
            id: `replaced-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            name: file.name,
            size: file.size,
            url: newUrl,
            file,
            isExisting: false,
          };
        }
        return item;
      })
    );
    e.target.value = '';
  };

  const handleRemoveAttachment = (id: string) => {
    setEditAttachments(prev => prev.filter(item => item.id !== id));
    setAttachmentError('');
  };

  const handleOpenPreview = (url: string, name: string) => {
    if (!url) return;
    setPreviewModalUrl(url);
    setPreviewModalTitle(name);
  };

  const handleEditSubmit = async () => {
    if (!ticket) return;

    setIsSubmitting(true);
    try {
      const filesToUpload: File[] = [];
      const existingAttachmentsToKeep: any[] = [];

      for (const item of editAttachments) {
        if (item.file) {
          filesToUpload.push(item.file);
        } else if (item.isExisting && item.url) {
          if (item.originalAttachment) {
            existingAttachmentsToKeep.push(item.originalAttachment);
          } else if (item.path) {
            existingAttachmentsToKeep.push(item.path);
          }

          try {
            const response = await api.get(item.url, { responseType: 'blob' });
            if (response.data) {
              const fileType = response.data.type || 'application/octet-stream';
              const file = new File([response.data], item.name, {
                type: fileType,
              });
              filesToUpload.push(file);
            }
          } catch (err) {
            console.error('Error converting existing attachment to File:', err);
          }
        }
      }

      const response = await supportTicketAPI.updateTicket(ticket.ticketId, {
        category: editCategory as
          | 'Trading Issues'
          | 'Account Issues'
          | 'Payment Issues'
          | 'Technical Issues'
          | 'Other',
        issueDescription: editDescription,
        accountNumber: editAccountNumber,
        attachments: filesToUpload.length > 0 ? filesToUpload : undefined,
        existingAttachments:
          existingAttachmentsToKeep.length > 0
            ? existingAttachmentsToKeep
            : undefined,
      });

      if (response.success) {
        toast.success(response.message || 'Ticket updated successfully');
        setIsEditModalOpen(false);
        setEditAttachments([]);
        if (onEdit) {
          onEdit(ticket.ticketId);
        }
      } else {
        if (
          response.message?.includes('Cannot edit ticket') ||
          response.message?.includes('already resolved') ||
          response.message?.includes('cannot be modified')
        ) {
          toast.error(
            response.message || 'Cannot edit resolved or closed tickets'
          );
        } else {
          toast.error(response.error || 'Failed to update ticket');
        }
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error updating support ticket';

      if (
        errorMessage?.includes('Cannot edit ticket') ||
        errorMessage?.includes('already resolved') ||
        errorMessage?.includes('cannot be modified')
      ) {
        toast.error(errorMessage);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = () => {
    setEditCategory(ticket.category);
    setEditDescription(ticket.issueDescription);
    setEditAccountNumber(ticket.accountNumber || '');
    setAttachmentError('');

    const initialItems: EditableAttachment[] = (ticket.attachments || []).map(
      (att: any, idx: number) => {
        let path = '';
        let name = '';
        let size: number | undefined = undefined;

        if (typeof att === 'string') {
          path = att;
          name = att.split(/[\\/]/).pop() || att;
        } else if (att && typeof att === 'object') {
          path = att.url || att.path || att.filename || '';
          name =
            att.originalName ||
            att.filename ||
            att.name ||
            `Attachment ${idx + 1}`;
          size = att.size;
        }

        const url = getAttachmentUrl(path);

        return {
          id: `existing-${idx}-${path}`,
          name,
          size,
          url,
          path,
          originalAttachment: att,
          isExisting: true,
        };
      }
    );

    setEditAttachments(initialItems);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ticket) return;

    setIsDeleting(true);
    try {
      const response = await supportTicketAPI.deleteTicket(ticket.ticketId);

      if (response.success) {
        toast.success(response.message || 'Ticket deleted successfully');
        setIsDeleteDialogOpen(false);
        if (onDelete) {
          onDelete(ticket.ticketId);
        }
      } else {
        toast.error(response.message || 'Failed to delete ticket');
      }
    } catch (error) {
      console.error('Error deleting ticket:', error);
      let errorMessage = 'Error deleting support ticket';

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { data?: { message?: string; error?: string } };
          message?: string;
        };
        if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        } else if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const getTruncatedText = (text: string, maxLength: number = 100) => {
    if (!text) return '';
    const cleaned = text.replace(/\s+/g, ' ');
    if (cleaned.length <= maxLength) return cleaned;
    return cleaned.slice(0, maxLength).trim() + '...';
  };

  return (
    <>
      <Card className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-primary">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono bg-muted px-2 py-1 rounded">
                  {ticket.ticketId}
                </span>
              </div>
              <CardTitle className="text-lg leading-tight">
                {ticket.subject}
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  className={getStatusColor(ticket.category)}
                  variant="secondary"
                >
                  {ticket.category}
                </Badge>
                <Badge
                  className={getStatusColor(ticket.priority, 'priority')}
                  variant="outline"
                >
                  {capitalizeFirst(ticket.priority)}
                </Badge>
                <Badge className={getStatusColor(ticket.status)}>
                  {capitalizeFirst(ticket.status)}
                </Badge>
              </div>
            </div>

            {!(ticket.status === 'Resolved' || ticket.status === 'Closed') && (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEditClick}
                  className="h-8 w-8 p-0"
                  title="Edit Ticket"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteClick}
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                  title="Delete Ticket"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line break-words">
              {ticket.issueDescription &&
              ticket.issueDescription.length > 100 ? (
                <>
                  {!isDescriptionExpanded
                    ? getTruncatedText(ticket.issueDescription, 100)
                    : ticket.issueDescription}{' '}
                  <Button
                    type="button"
                    variant="link"
                    onClick={() =>
                      setIsDescriptionExpanded(!isDescriptionExpanded)
                    }
                    className="h-auto p-0 text-xs font-semibold text-primary hover:underline inline-flex items-center gap-0.5 focus:outline-none align-baseline ml-1"
                  >
                    {isDescriptionExpanded ? (
                      <>
                        Show Less <ChevronUp className="h-3.5 w-3.5" />
                      </>
                    ) : (
                      <>
                        Show More <ChevronDown className="h-3.5 w-3.5" />
                      </>
                    )}
                  </Button>
                </>
              ) : (
                ticket.issueDescription
              )}
            </p>
          </div>

          <div className="space-y-3">
            {ticket.attachments && ticket.attachments.length > 0 && (
              <div className="space-y-2 my-3">
                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Paperclip className="h-3.5 w-3.5" />
                  <span>Attachments ({ticket.attachments.length})</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {ticket.attachments.map((att: any, idx: number) => {
                    const path =
                      typeof att === 'string'
                        ? att
                        : att.path || att.filename || '';
                    const name =
                      typeof att === 'string'
                        ? att.split(/[\\/]/).pop() || att
                        : att.originalName ||
                          att.filename ||
                          `Attachment ${idx + 1}`;
                    const url = getAttachmentUrl(path);
                    const isImg =
                      isImageFile(name) ||
                      (typeof att === 'object' &&
                        att?.mimeType?.startsWith('image/'));
                    const sizeStr =
                      typeof att === 'object' && att.size
                        ? formatFileSize(att.size)
                        : '';

                    return isImg && url ? (
                      <a
                        key={idx}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group block border border-border rounded-md overflow-hidden bg-muted/20 hover:border-primary/50 transition-all shadow-sm"
                      >
                        <div className="h-28 w-full bg-muted/40 flex items-center justify-center overflow-hidden">
                          <AttachmentImage
                            src={url}
                            alt={name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                          />
                        </div>
                        <div className="p-1.5 bg-background flex items-center justify-between text-[11px] border-t border-border">
                          <span
                            className="truncate flex-1 font-medium text-foreground"
                            title={name}
                          >
                            {name}
                          </span>
                          {sizeStr && (
                            <span className="text-[10px] text-muted-foreground ml-1 flex-shrink-0">
                              {sizeStr}
                            </span>
                          )}
                        </div>
                      </a>
                    ) : (
                      <a
                        key={idx}
                        href={url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 rounded-md border border-border bg-background hover:bg-muted/40 hover:border-primary/50 transition-all text-xs shadow-sm"
                      >
                        <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p
                            className="truncate font-medium text-foreground"
                            title={name}
                          >
                            {name}
                          </p>
                          {sizeStr && (
                            <p className="text-[10px] text-muted-foreground">
                              {sizeStr}
                            </p>
                          )}
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
            {ticket.message && (
              <div className="mt-3 p-3.5 bg-blue-50/40 border border-blue-100 rounded-lg border-l-4 border-l-blue-500 space-y-2">
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span className="text-sm font-semibold text-blue-700">
                    Admin Response
                  </span>
                </div>
                <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-line break-words w-full">
                  {ticket.message.length > 100 ? (
                    <>
                      {!isAdminResponseExpanded
                        ? getTruncatedText(ticket.message, 100)
                        : ticket.message}{' '}
                      <Button
                        type="button"
                        variant="link"
                        onClick={() =>
                          setIsAdminResponseExpanded(!isAdminResponseExpanded)
                        }
                        className="h-auto p-0 text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-0.5 focus:outline-none align-baseline ml-1"
                      >
                        {isAdminResponseExpanded ? (
                          <>
                            Show Less <ChevronUp className="h-3.5 w-3.5" />
                          </>
                        ) : (
                          <>
                            Show More <ChevronDown className="h-3.5 w-3.5" />
                          </>
                        )}
                      </Button>
                    </>
                  ) : (
                    ticket.message
                  )}
                </p>
              </div>
            )}
            <div className="flex items-center justify-between text-xs pt-1 border-t border-border/40">
              <div className="flex items-center gap-4 text-muted-foreground">
                <div className="flex items-center gap-1">
                  <User className="h-3.5 w-3.5" />
                  <span className="truncate max-w-32">
                    {ticket.creator.fullName}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  <span>{formatDate(ticket.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          onOpenAutoFocus={e => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Edit Ticket
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <input
                  id="subject"
                  type="text"
                  disabled
                  className="w-full p-3 border border-border rounded-md bg-muted"
                  value={ticket.subject}
                  placeholder="Enter ticket subject"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <SourceCombobox
                  id="category"
                  items={SUPPORT_CATEGORIES.map(option => option.label)}
                  placeholder="Select category"
                  value={editCategoryLabel}
                  onChange={value => {
                    const option = SUPPORT_CATEGORIES.find(
                      item => item.label === value
                    );
                    if (option) {
                      setEditCategory(option.value);
                    }
                  }}
                  emptyText="No categories found."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="accountNumber">Account Number</Label>
                {loadingMt5Accounts ? (
                  <input
                    id="accountNumber"
                    type="text"
                    className="w-full p-3 border border-border rounded-md bg-muted"
                    value={editAccountNumber}
                    disabled
                    placeholder="Loading trading accounts..."
                  />
                ) : mt5AccountOptions.length > 0 ? (
                  <SourceCombobox
                    id="accountNumber"
                    items={mt5AccountOptions.map(option => option.label)}
                    placeholder="Select trading account"
                    value={selectedMt5AccountLabel || ''}
                    onChange={value => {
                      const option = mt5AccountOptions.find(
                        item => item.label === value
                      );
                      if (option) {
                        setEditAccountNumber(option.value);
                      }
                    }}
                    emptyText="No trading accounts found."
                  />
                ) : (
                  <input
                    id="accountNumber"
                    type="text"
                    className="w-full p-3 border border-border rounded-md bg-background"
                    value={editAccountNumber}
                    onChange={e => setEditAccountNumber(e.target.value)}
                    placeholder="e.g., DEM-00123456"
                  />
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                className="w-full p-3 border border-border rounded-md bg-background min-h-[100px]"
                value={editDescription}
                onChange={e => setEditDescription(e.target.value)}
                placeholder="Describe your issue..."
              />
            </div>

            {/* Attachments Section */}
            <div className="space-y-3 pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold flex items-center gap-1.5">
                  <Paperclip className="h-4 w-4 text-primary" />
                  Attached Documents & Screenshots ({editAttachments.length}/2)
                </Label>
                <span className="text-xs text-muted-foreground">
                  Max 2 files, up to 5MB each
                </span>
              </div>

              {/* Grid of Attachment Preview Cards */}
              {editAttachments.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {editAttachments.map(item => {
                    const isImg =
                      isImageFile(item.name) && !failedImageUrls[item.url];
                    const sizeStr = item.size ? formatFileSize(item.size) : '';

                    return (
                      <div
                        key={item.id}
                        className="border border-border rounded-lg overflow-hidden bg-card hover:border-primary/40 transition-all shadow-sm flex flex-col relative group"
                      >
                        {/* Direct Image or Document Preview Container */}
                        {isImg && item.url ? (
                          <div
                            onClick={() =>
                              handleOpenPreview(item.url, item.name)
                            }
                            className="h-36 w-full bg-muted/40 flex items-center justify-center overflow-hidden cursor-pointer relative"
                            title="Click to view preview"
                          >
                            <AttachmentImage
                              src={item.url}
                              alt={item.name}
                              onError={() => handleImageError(item.url)}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            />
                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-medium gap-1">
                              <Eye className="h-4 w-4" />
                              View Preview
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() =>
                              handleOpenPreview(item.url, item.name)
                            }
                            className="h-36 w-full bg-muted/30 flex flex-col items-center justify-center p-4 cursor-pointer text-center"
                          >
                            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-2">
                              <FileText className="h-6 w-6" />
                            </div>
                            <p
                              className="text-xs font-medium truncate max-w-full text-foreground"
                              title={item.name}
                            >
                              {item.name}
                            </p>
                            {sizeStr && (
                              <p className="text-[10px] text-muted-foreground mt-0.5">
                                {sizeStr}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Bottom Card Controls: Replace File & Cancel / Delete Button */}
                        <div className="p-2 bg-muted/30 flex items-center justify-between border-t border-border gap-2">
                          <div className="flex-1 min-w-0">
                            <p
                              className="text-xs font-medium truncate text-foreground"
                              title={item.name}
                            >
                              {item.name}
                            </p>
                            <p className="text-[10px] text-muted-foreground">
                              {item.isExisting ? 'Existing' : 'New Attachment'}
                              {sizeStr ? ` • ${sizeStr}` : ''}
                            </p>
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0">
                            {/* Replace File Button */}
                            <label
                              htmlFor={`replace-file-${item.id}`}
                              className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                              title="Replace file"
                            >
                              <Edit className="h-3.5 w-3.5" />
                              <input
                                id={`replace-file-${item.id}`}
                                type="file"
                                accept="image/*,.pdf,.doc,.docx,.txt"
                                className="hidden"
                                onChange={e => handleReplaceFile(item.id, e)}
                              />
                            </label>

                            {/* Remove / Cancel Button */}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveAttachment(item.id)}
                              className="h-7 w-7 p-0 rounded-md hover:bg-red-50 text-red-500 hover:text-red-700 transition-colors"
                              title="Cancel / Delete attachment"
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add Attachment Button (if less than 2 files) */}
              {editAttachments.length < 2 && (
                <div className="space-y-2">
                  <Label
                    htmlFor="add-attachment-input"
                    className="text-xs font-medium text-muted-foreground block"
                  >
                    {editAttachments.length === 0
                      ? 'Add Attachments (up to 2 files):'
                      : 'Add Second Attachment:'}
                  </Label>
                  <input
                    id="add-attachment-input"
                    type="file"
                    multiple={editAttachments.length === 0}
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    onChange={handleAddFiles}
                    className="w-full text-xs text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer border border-border rounded-md p-1.5 bg-background"
                  />
                </div>
              )}

              {attachmentError && (
                <p className="text-xs text-red-500 font-medium">
                  {attachmentError}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsEditModalOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button onClick={handleEditSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Updating...' : 'Update Ticket'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* In-App Image Preview Lightbox Modal */}
      <Dialog
        open={!!previewModalUrl}
        onOpenChange={() => setPreviewModalUrl(null)}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] p-4 overflow-hidden flex flex-col items-center">
          <DialogHeader className="w-full pb-2 border-b border-border flex flex-row items-center justify-between">
            <DialogTitle className="text-sm font-semibold truncate pr-4">
              {previewModalTitle || 'Attachment Preview'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 w-full max-h-[75vh] flex items-center justify-center p-2 bg-black/5 rounded-lg overflow-auto mt-2">
            {previewModalUrl &&
              (isImageFile(previewModalTitle) ? (
                <AttachmentImage
                  src={previewModalUrl}
                  alt={previewModalTitle}
                  className="max-w-full max-h-[70vh] object-contain rounded shadow"
                />
              ) : (
                <iframe
                  src={previewModalUrl}
                  title={previewModalTitle}
                  className="w-full h-[65vh] border-0 rounded"
                />
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteDialogOpen}
        title="Delete Support Ticket"
        message={`Are you sure you want to delete this ticket?: ${ticket.ticketId}`}
        confirmText="Yes"
        cancelText="No"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setIsDeleteDialogOpen(false)}
        type="danger"
        loading={isDeleting}
        loadingText="Deleting ticket..."
      />
    </>
  );
};

export default TicketCard;
